import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {ChevronRightIcon, SearchIcon} from '@heroicons/react/outline';
import {Spinner} from '@tyrannosaurustech/ui';

import {ContentWrapper} from '../../../components/ContentWrapper';
import {TitleM, ParagraphS} from '../../../components/atoms/Typography';
import {InitialsAvatar} from '../../../components/atoms/Avatar';
import {Theme} from '../../../components/atoms/theme';
import {useGetAuditStatsQuery} from '../../../redux/reduxQuery/auditApi';
import {
  BUCKETS,
  RANGE_OPTIONS,
  UserStats,
  computeRollingTrendWindow,
  computeWindow,
  fmtDateTime,
  fmtDayShort,
  rollingSumsAt,
  summarize,
} from './constants';
import {ChartStyles, Legend, MixBar, Sparkline} from './charts';

const initialsOf = (name: string) =>
  (name || '?')
    .split(' ')
    .map((s) => s.charAt(0))
    .slice(0, 2)
    .join('');

export default function TeamActivity() {
  const navigate = useNavigate();
  const [rangeDays, setRangeDays] = useState(14);
  const [search, setSearch] = useState('');
  const [bucketKey, setBucketKey] = useState<string | null>(null);
  const activeBucket = BUCKETS.find((b) => b.key === bucketKey) || null;

  const win = useMemo(() => computeWindow(rangeDays), [rangeDays]);
  const {data, isLoading} = useGetAuditStatsQuery({
    startDate: win.startDate,
    endDate: win.endDate,
  });

  // 12-month rolling trend (replaces the old period-over-period "vs prior" percent,
  // which was too noisy at short ranges — e.g. a single quiet week could read as an
  // 18600% swing). Independent of the rangeDays toggle above: always a full year back.
  const trendWin = useMemo(() => computeRollingTrendWindow(), []);
  const {data: trendData} = useGetAuditStatsQuery({
    startDate: trendWin.startDate,
    endDate: trendWin.endDate,
  });

  const users: UserStats[] = useMemo(() => data?.users ?? [], [data]);
  const trendUsers: UserStats[] = useMemo(() => trendData?.users ?? [], [trendData]);

  const rollingTrends = useMemo(() => {
    const map: Record<string, number[]> = {};
    trendUsers.forEach((u) => {
      const s = summarize(u, trendWin.dayList);
      const daily = bucketKey ? s.dailyByBucket[bucketKey] : s.dailyTotals;
      map[u.userId] = rollingSumsAt(trendWin.dayList, daily, trendWin.sampleDayKeys);
    });
    return map;
  }, [trendUsers, trendWin, bucketKey]);

  const summaries = useMemo(() => {
    const map: Record<string, ReturnType<typeof summarize>> = {};
    users.forEach((u) => (map[u.userId] = summarize(u, win.dayList)));
    return map;
  }, [users, win.dayList]);

  // When a bucket is selected, every number on the page scopes to it;
  // with nothing selected this reduces to the overall totals.
  const metricOf = useMemo(
    () => (u: UserStats) => bucketKey ? summaries[u.userId]?.totalsByBucket[bucketKey] || 0 : u.total,
    [summaries, bucketKey],
  );

  const ranked = useMemo(() => {
    if (!bucketKey) return users;
    return users.filter((u) => metricOf(u) > 0).sort((a, b) => metricOf(b) - metricOf(a));
  }, [users, bucketKey, metricOf]);

  const rollup = useMemo(() => {
    const grand = users.reduce((a, u) => a + metricOf(u), 0);
    const dayTotals = win.dayList.map((_, i) =>
      users.reduce(
        (a, u) => a + (bucketKey ? summaries[u.userId]?.dailyByBucket[bucketKey]?.[i] || 0 : summaries[u.userId]?.dailyTotals[i] || 0),
        0,
      ),
    );
    const busiestIdx = dayTotals.indexOf(Math.max(...dayTotals, 0));
    const mix = BUCKETS.map((b) => ({
      key: b.key,
      label: b.label,
      color: b.color,
      value: users.reduce((a, u) => a + (summaries[u.userId]?.totalsByBucket[b.key] || 0), 0),
    }));
    const allMixTotal = mix.reduce((a, m) => a + m.value, 0);
    const topMix = bucketKey ? mix.find((m) => m.key === bucketKey)! : [...mix].sort((a, b) => b.value - a.value)[0];
    return {grand, dayTotals, busiestIdx, mix, allMixTotal, topMix};
  }, [users, summaries, win.dayList, bucketKey, metricOf]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? ranked.filter((u) => (u.userName || '').toLowerCase().includes(q)) : ranked;
  }, [ranked, search]);

  return (
    <ContentWrapper>
      <ChartStyles />
      <div className="flex flex-wrap items-end justify-between gap-4 pt-6 pb-4">
        <div>
          <TitleM style={{margin: 0}}>Team Activity</TitleM>
          <ParagraphS style={{margin: '4px 0 0', color: '#6C6579'}}>
            Who did what across RAYNA — every action in the audit log, attributed to the person who made it. Click a category below to focus
            on one type of action.
          </ParagraphS>
        </div>
        <div className="flex rounded-lg border border-gray-300 bg-white p-[3px]">
          {RANGE_OPTIONS.map((d) => (
            <button
              key={d}
              onClick={() => setRangeDays(d)}
              aria-pressed={rangeDays === d}
              className="rounded-md px-3 py-1 text-xs font-semibold"
              style={
                rangeDays === d
                  ? {
                      background: '#F7E9FB',
                      color: Theme.colors.primaries_000,
                    }
                  : {color: '#6C6579'}
              }
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Spinner colorClassName="text-primary" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 pb-5 lg:grid-cols-4">
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
                {activeBucket ? activeBucket.label : 'Total actions'} · {rangeDays}d
              </div>
              <div className="mt-1 text-2xl font-bold">{rollup.grand}</div>
              <div className="text-xs text-gray-500">
                across {ranked.length} team member
                {ranked.length === 1 ? '' : 's'}
              </div>
            </div>
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
                {activeBucket ? `Most ${activeBucket.label.toLowerCase()}` : 'Most active'}
              </div>
              <div className="mt-1 truncate text-2xl font-bold">{ranked[0]?.userName || '—'}</div>
              <div className="text-xs text-gray-500">
                {ranked[0] ? `${metricOf(ranked[0])} ${activeBucket ? activeBucket.label.toLowerCase() : 'actions'}` : 'no activity yet'}
              </div>
            </div>
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Busiest day</div>
              <div className="mt-1 text-2xl font-bold">{rollup.grand ? fmtDayShort(win.dayList[rollup.busiestIdx]) : '—'}</div>
              <div className="text-xs text-gray-500">
                {rollup.grand
                  ? `${rollup.dayTotals[rollup.busiestIdx]} ${activeBucket ? activeBucket.label.toLowerCase() : 'actions'}`
                  : 'no activity yet'}
              </div>
            </div>
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Action mix</div>
              <div className="mt-3">
                <MixBar parts={rollup.mix} height={8} selectedKey={bucketKey} onSelect={setBucketKey} />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {rollup.allMixTotal && rollup.topMix.value
                  ? `${rollup.topMix.label} ${activeBucket ? 'selected' : 'lead'} : ${Math.round(
                      (rollup.topMix.value / rollup.allMixTotal) * 100,
                    )}% of all actions`
                  : 'no activity yet'}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-3">
              <Legend
                items={BUCKETS.map((b) => ({
                  key: b.key,
                  label: b.label,
                  color: b.color,
                }))}
                selectedKey={bucketKey}
                onSelect={setBucketKey}
              />
              <label className="flex min-w-[220px] items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5">
                <SearchIcon width={14} color="#9A93A8" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search team members…"
                  aria-label="Search team members"
                  className="w-full border-0 text-sm outline-none"
                />
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-[10.5px] font-bold uppercase tracking-wider text-gray-400">
                    <th className="px-4 py-2">#</th>
                    <th className="px-4 py-2">Member</th>
                    <th className="px-4 py-2">Action mix</th>
                    <th className="px-4 py-2 text-right">{activeBucket ? activeBucket.label : 'Actions'}</th>
                    <th className="px-4 py-2" title={`Actions per day, last ${rangeDays} days`}>
                      Daily trend
                    </th>
                    <th className="px-4 py-2" title="Rolling 30-day total, sampled weekly, last 12 months">
                      12-mo trend
                    </th>
                    <th className="px-4 py-2">Last active</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-400">
                        {users.length === 0
                          ? 'No activity recorded in this window.'
                          : ranked.length === 0
                          ? `No ${activeBucket?.label.toLowerCase()} recorded in this window.`
                          : 'No team members match that search.'}
                      </td>
                    </tr>
                  )}
                  {visible.map((u) => {
                    const rank = ranked.indexOf(u) + 1;
                    const s = summaries[u.userId];
                    const open = () => navigate(`/admin/team-activity/${u.userId}`);
                    return (
                      <tr
                        key={u.userId}
                        onClick={open}
                        onKeyDown={(e) => e.key === 'Enter' && open()}
                        tabIndex={0}
                        aria-label={`Open activity for ${u.userName}`}
                        className="ta-row cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
                      >
                        <td
                          className="px-4 py-3 font-mono text-xs"
                          style={{
                            color: rank === 1 ? Theme.colors.primaries_000 : '#9A93A8',
                            fontWeight: rank === 1 ? 700 : 400,
                          }}
                        >
                          {rank}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <InitialsAvatar
                              text={initialsOf(u.userName)}
                              background={Theme.colors.primaries_000}
                              hover={Theme.colors.primaries_000}
                              size={36}
                            />
                            <span className="text-sm font-semibold">{u.userName || 'Unknown user'}</span>
                          </div>
                        </td>
                        <td className="min-w-[170px] px-4 py-3">
                          <MixBar
                            parts={BUCKETS.map((b) => ({
                              key: b.key,
                              label: b.label,
                              color: b.color,
                              value: s?.totalsByBucket[b.key] || 0,
                            }))}
                            selectedKey={bucketKey}
                          />
                        </td>
                        <td className="px-4 py-3 text-right text-base font-bold tabular-nums">{metricOf(u)}</td>
                        <td className="px-4 py-3">
                          <Sparkline
                            values={(bucketKey ? s?.dailyByBucket[bucketKey] : s?.dailyTotals) || []}
                            color={activeBucket?.color || Theme.colors.primaries_000}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Sparkline values={rollingTrends[u.userId] || []} color={activeBucket?.color || Theme.colors.primaries_000} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">{fmtDateTime(u.lastActive)}</td>
                        <td className="px-4 py-3 text-gray-300">
                          <ChevronRightIcon width={14} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <ParagraphS style={{color: '#9A93A8', margin: '14px 2px 32px'}}>
            Sourced from the audit log. Click a teammate to open their full activity profile.
          </ParagraphS>
        </>
      )}
    </ContentWrapper>
  );
}
